// src/pages/Payroll.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Table, Form, Button } from 'react-bootstrap';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  LayoutDashboard, Sliders, History, Users, DollarSign,
  Award, TrendingUp, TrendingDown, Car, FileText, CheckCircle2,
  Clock, Edit3, Save, X, PieChart, ShieldCheck, Upload, Download
} from 'lucide-react';
import SalarySlipModal from '../components/payroll/SalarySlipModal';
import PayrollUploadModal from '../components/payroll/PayrollUploadModal';
import './Payroll.css';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmt = v => `PKR ${Number(v || 0).toLocaleString()}`;

function TableSkeleton({ rows = 5, cols = 8 }) {
  return (
    <div className="payroll-table-card">
      <Table responsive className="payroll-table mb-0">
        <thead>
          <tr>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i}><div className="crm-skeleton-pulse" style={{ width: '80%', height: 16, borderRadius: 4 }} /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r}>
              {Array.from({ length: cols }).map((_, c) => (
                <td key={c}><div className="crm-skeleton-pulse" style={{ width: c === 0 ? '60%' : '85%', height: 18, borderRadius: 4 }} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function PayrollOverviewSkeleton() {
  return (
    <div className="d-flex flex-column gap-3" data-tour="payroll-admin">
      <div className="payroll-stats-grid">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div className="payroll-stat-card" key={i} style={{ height: 98 }}>
            <div className="crm-skeleton-pulse mb-2" style={{ width: '50%', height: 12, borderRadius: 4 }} />
            <div className="crm-skeleton-pulse" style={{ width: '75%', height: 24, borderRadius: 6 }} />
          </div>
        ))}
      </div>
      <div className="payroll-summary-card p-4">
        <div className="crm-skeleton-pulse mb-3" style={{ width: 180, height: 20, borderRadius: 4 }} />
        <div className="crm-skeleton-pulse" style={{ width: '100%', height: 120, borderRadius: 8 }} />
      </div>
    </div>
  );
}

function PayrollOverview({ user, employees, onOpenSlip, onOpenUpload, reloadKey }) {
  const now       = new Date();
  const [empId,   setEmpId]   = useState('');
  const [month,   setMonth]   = useState(now.getMonth() + 1);
  const [year,    setYear]    = useState(now.getFullYear());
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState({});

  const isAdmin  = user?.role === 'Admin';
  const targetId = empId || (user?.id || user?._id);

  const load = useCallback(async () => {
    if (!targetId) return;
    setLoading(true);
    try {
      const res = await api.get(`/commissions/payroll/${targetId}?month=${month}&year=${year}`);
      setData(res.data);
      setForm({
        fixedSalary: res.data.fixedSalary || 0,
        commission: res.data.commission || 0,
        bonuses: res.data.bonuses || 0,
        deductions: res.data.deductions || 0,
        profitGenerated: res.data.profitGenerated || 0,
        carsSold: res.data.carsSold || 0,
        paymentStatus: res.data.paymentStatus || 'pending',
        notes: res.data.notes || '',
      });
    } catch {
      toast.error('Failed to load payroll');
    } finally {
      setLoading(false);
    }
  }, [targetId, month, year]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const save = async () => {
    try {
      await api.post(`/commissions/payroll/${targetId}`, { ...form, month, year });
      toast.success('Payroll saved');
      setEditing(false);
      load();
    } catch {
      toast.error('Save failed');
    }
  };

  const yearOptions = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];
  const visibleEmp  = user?.role === 'Admin' ? employees : employees.filter(e => e.manager === user?.name || e.email === user?.email);

  const fixedSal   = Number(form.fixedSalary || 0);
  const commAmt    = Number(form.commission || 0);
  const bonusAmt   = Number(form.bonuses || 0);
  const deductAmt  = Number(form.deductions || 0);
  const grossPay   = fixedSal + commAmt + bonusAmt;
  const netEarnings = grossPay - deductAmt;

  const statItems = [
    { label: 'Fixed Salary',    key: 'fixedSalary',     val: fixedSal,   icon: DollarSign, isCount: false },
    { label: 'Commission',       key: 'commission',      val: commAmt,    icon: Award,      isCount: false },
    { label: 'Bonuses',          key: 'bonuses',         val: bonusAmt,   icon: TrendingUp, isCount: false },
    { label: 'Deductions',       key: 'deductions',      val: deductAmt,  icon: TrendingDown, isCount: false },
    { label: 'Profit Generated', key: 'profitGenerated', val: Number(form.profitGenerated || 0), icon: PieChart, isCount: false },
    { label: 'Cars Sold',        key: 'carsSold',        val: Number(form.carsSold || 0), icon: Car, isCount: true },
  ];

  return (
    <div className="d-flex flex-column gap-3" data-tour="payroll-admin">
      {/* ── Period & Employee Selectors Bar ── */}
      <div className="payroll-selectors-bar">
        <div className="payroll-select-group">
          {(isAdmin || user?.role === 'Manager') && (
            <div className="payroll-select-item">
              <span className="payroll-select-label">Employee</span>
              <Form.Select
                size="sm"
                className="payroll-select-input"
                style={{ minWidth: 200 }}
                value={empId}
                onChange={e => setEmpId(e.target.value)}
              >
                <option value="">— My Payroll —</option>
                {visibleEmp.map(e => (
                  <option key={e.id || e._id} value={e.id || e._id}>{e.name} ({e.role})</option>
                ))}
              </Form.Select>
            </div>
          )}

          <div className="payroll-select-item">
            <span className="payroll-select-label">Month</span>
            <Form.Select
              size="sm"
              className="payroll-select-input"
              value={month}
              onChange={e => setMonth(Number(e.target.value))}
            >
              {MONTHS.map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </Form.Select>
          </div>

          <div className="payroll-select-item">
            <span className="payroll-select-label">Year</span>
            <Form.Select
              size="sm"
              className="payroll-select-input"
              value={year}
              onChange={e => setYear(Number(e.target.value))}
            >
              {yearOptions.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </Form.Select>
          </div>
        </div>

        {data && (
          <div className="d-flex align-items-center gap-2">
            <span className={`payroll-status-badge ${form.paymentStatus === 'paid' ? 'paid' : 'pending'}`}>
              {form.paymentStatus === 'paid' ? (
                <><CheckCircle2 size={12} /> Paid</>
              ) : (
                <><Clock size={12} /> Pending</>
              )}
            </span>

            {/* Pay Slip View & Download */}
            <Button
              size="sm"
              variant="outline-primary"
              className="d-flex align-items-center gap-1"
              style={{ borderRadius: 8, fontSize: 12.5 }}
              onClick={() => onOpenSlip && onOpenSlip({ ...data, month, year })}
              title="Download Official Salary Pay Slip"
            >
              <FileText size={13} /> Pay Slip
            </Button>

            {isAdmin && !editing && (
              <Button
                size="sm"
                variant="outline-success"
                className="d-flex align-items-center gap-1"
                style={{ borderRadius: 8, fontSize: 12.5 }}
                onClick={onOpenUpload}
                title="Batch Import Excel or CSV Spreadsheet"
              >
                <Upload size={13} /> Import Excel
              </Button>
            )}

            {isAdmin && !editing && (
              <Button
                size="sm"
                variant="outline-secondary"
                className="d-flex align-items-center gap-1"
                style={{ borderRadius: 8, fontSize: 12.5 }}
                onClick={() => setEditing(true)}
              >
                <Edit3 size={13} /> Edit
              </Button>
            )}

            {isAdmin && editing && (
              <div className="d-flex gap-1">
                <Button
                  size="sm"
                  variant="success"
                  className="d-flex align-items-center gap-1"
                  style={{ borderRadius: 8, fontSize: 12.5 }}
                  onClick={save}
                >
                  <Save size={13} /> Save
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  style={{ borderRadius: 8, fontSize: 12.5 }}
                  onClick={() => setEditing(false)}
                >
                  <X size={13} />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <PayrollOverviewSkeleton />
      ) : data && (
        <>
          {/* ── KPI Metric Grid (Apple Pro Obsidian / Titanium) ── */}
          <div className="payroll-stats-grid">
            {statItems.map(({ label, key, val, icon: Icon, isCount }) => (
              <div className="payroll-stat-card" key={key}>
                <div className="payroll-stat-top">
                  <span className="payroll-stat-label">{label}</span>
                  <div className="payroll-stat-icon">
                    <Icon size={14} />
                  </div>
                </div>

                {editing && isAdmin ? (
                  <Form.Control
                    size="sm"
                    type="number"
                    value={form[key] ?? 0}
                    onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                    className="mt-2"
                  />
                ) : (
                  <div className="payroll-stat-value">
                    {isCount ? val : fmt(val)}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* ── Structured Payslip & Compensation Summary ── */}
          <div className="payroll-summary-card">
            <div className="payroll-summary-head">
              <div>
                <h4 className="payroll-summary-title">
                  {data.employee?.name || 'Employee'} — Compensation Statement
                </h4>
                <span className="small text-muted">
                  {data.employee?.role || 'Staff'} · Period: {MONTHS[month - 1]} {year}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="d-flex align-items-center gap-1"
                  style={{ borderRadius: 8, fontSize: 12, padding: '4px 10px' }}
                  onClick={() => onOpenSlip && onOpenSlip({ ...data, month, year })}
                >
                  <Download size={13} /> Download Slip
                </Button>
                <span className="badge rounded-pill" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', padding: '5px 12px', fontSize: '11.5px' }}>
                  <FileText size={12} className="me-1" style={{ opacity: 0.6 }} /> Statement #{year}{String(month).padStart(2, '0')}
                </span>
              </div>
            </div>

            <div className="payroll-summary-grid">
              {/* Earnings Column */}
              <div className="payroll-summary-col">
                <div className="payroll-col-header">Earnings & Allowances</div>
                <div className="payroll-slip-row">
                  <span className="payroll-slip-label">Fixed Base Salary</span>
                  <span className="payroll-slip-num">{fmt(fixedSal)}</span>
                </div>
                <div className="payroll-slip-row">
                  <span className="payroll-slip-label">Deal & Sales Commission</span>
                  <span className="payroll-slip-num">{fmt(commAmt)}</span>
                </div>
                <div className="payroll-slip-row">
                  <span className="payroll-slip-label">Performance Bonuses</span>
                  <span className="payroll-slip-num">{fmt(bonusAmt)}</span>
                </div>
                <div className="payroll-slip-row total-row">
                  <span className="payroll-slip-label">Gross Earnings</span>
                  <span className="payroll-slip-num">{fmt(grossPay)}</span>
                </div>
              </div>

              {/* Deductions Column */}
              <div className="payroll-summary-col" style={{ borderLeft: '1px solid var(--border)' }}>
                <div className="payroll-col-header">Deductions & Adjustments</div>
                <div className="payroll-slip-row">
                  <span className="payroll-slip-label">Advances & Deductions</span>
                  <span className="payroll-slip-num text-danger">{fmt(deductAmt)}</span>
                </div>
                <div className="payroll-slip-row">
                  <span className="payroll-slip-label">Statutory Withholdings</span>
                  <span className="payroll-slip-num">PKR 0</span>
                </div>
                <div className="payroll-slip-row">
                  <span className="payroll-slip-label">Other Penalties</span>
                  <span className="payroll-slip-num">PKR 0</span>
                </div>
                <div className="payroll-slip-row total-row">
                  <span className="payroll-slip-label">Total Deductions</span>
                  <span className="payroll-slip-num text-danger">-{fmt(deductAmt)}</span>
                </div>
              </div>
            </div>

            {/* Net Payout Bar */}
            <div className="payroll-net-card">
              <div>
                <div className="payroll-net-label">Net Payable Payout</div>
                <div className="payroll-net-amount">{fmt(netEarnings)}</div>
              </div>

              {editing && isAdmin && (
                <div className="d-flex align-items-center gap-2" style={{ minWidth: 220 }}>
                  <span className="small text-muted fw-semibold">Payment Status:</span>
                  <Form.Select
                    size="sm"
                    value={form.paymentStatus}
                    onChange={e => setForm(p => ({ ...p, paymentStatus: e.target.value }))}
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                  </Form.Select>
                </div>
              )}
            </div>
          </div>

          {/* ── Period Performance Contribution Note ── */}
          <div className="payroll-summary-card p-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <ShieldCheck size={18} style={{ color: 'var(--primary)' }} />
                <div>
                  <div className="fw-semibold" style={{ fontSize: 13 }}>
                    Monthly Sales & Pipeline Contribution
                  </div>
                  <div className="text-muted small">
                    {Number(form.carsSold || 0)} vehicle sale(s) closed in {MONTHS[month - 1]} {year}, generating {fmt(form.profitGenerated)} gross company profit.
                  </div>
                </div>
              </div>
              <span className="badge" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: '11px' }}>
                Automated Commission Sync
              </span>
            </div>

            {editing && isAdmin && (
              <Form.Group className="mt-3">
                <Form.Label className="small fw-semibold">Payroll Notes & Memo</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  size="sm"
                  value={form.notes}
                  placeholder="Add administrative notes for this payroll entry..."
                  onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                />
              </Form.Group>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function PayrollHistory({ user, employees, onOpenSlip }) {
  const [empId,   setEmpId]   = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const targetId   = empId || (user?.id || user?._id);
  const visibleEmp = user?.role === 'Admin' ? employees : employees.filter(e => e.manager === user?.name || e.email === user?.email);

  useEffect(() => {
    if (!targetId) return;
    setLoading(true);
    api.get(`/commissions/history/${targetId}`)
      .then(r => setHistory(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [targetId]);

  return (
    <div className="d-flex flex-column gap-3" data-tour="payroll-admin">
      {user?.role !== 'Agent' && (
        <div style={{ maxWidth: 280 }}>
          <Form.Select
            size="sm"
            className="payroll-select-input"
            value={empId}
            onChange={e => setEmpId(e.target.value)}
          >
            <option value="">— My History —</option>
            {visibleEmp.map(e => (
              <option key={e.id || e._id} value={e.id || e._id}>{e.name}</option>
            ))}
          </Form.Select>
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={4} cols={9} />
      ) : (
        <div className="payroll-table-card">
          <Table responsive hover className="payroll-table mb-0">
            <thead>
              <tr>
                <th>Month</th>
                <th>Fixed</th>
                <th>Commission</th>
                <th>Bonuses</th>
                <th>Deductions</th>
                <th>Total Earnings</th>
                <th>Cars Sold</th>
                <th>Status</th>
                <th style={{ width: 90, textAlign: 'center' }}>Slip</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">
                    No payroll history found for this account.
                  </td>
                </tr>
              ) : (
                history.map(h => (
                  <tr key={`${h.year}-${h.month}`}>
                    <td className="fw-semibold">{MONTHS[(h.month || 1) - 1]} {h.year}</td>
                    <td>{fmt(h.fixedSalary)}</td>
                    <td>{fmt(h.commission)}</td>
                    <td>{fmt(h.bonuses)}</td>
                    <td className="text-danger">{fmt(h.deductions)}</td>
                    <td className="fw-bold" style={{ color: 'var(--success)' }}>{fmt(h.totalEarnings)}</td>
                    <td>{h.carsSold || 0}</td>
                    <td>
                      <span className={`payroll-status-badge ${h.paymentStatus === 'paid' ? 'paid' : 'pending'}`}>
                        {h.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="d-inline-flex align-items-center gap-1"
                        style={{ borderRadius: 6, fontSize: 11.5, padding: '3px 8px' }}
                        onClick={() => onOpenSlip && onOpenSlip({
                          ...h,
                          employee: { id: h.employeeId, name: h.employeeName || user?.name, role: h.employeeRole || user?.role }
                        })}
                        title="View & Download Pay Slip"
                      >
                        <FileText size={12} /> Slip
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

function PayrollSettings({ employees, settingsMap, setSettingsMap }) {
  const save = async (emp) => {
    try {
      const c = settingsMap[emp.email] || {};
      await api.put(`/commissions/settings/${encodeURIComponent(emp.email)}`, {
        fixedSalary: Number(c.fixedSalary || 0),
        perCustomerCommission: Number(c.perCustomerCommission || 0),
        commissionType: c.commissionType || 'flat',
      });
      toast.success(`Saved for ${emp.name}`);
    } catch {
      toast.error('Save failed');
    }
  };

  const onChange = (email, field, value) => {
    setSettingsMap(prev => ({ ...prev, [email]: { ...(prev[email] || {}), [field]: value } }));
  };

  return (
    <div className="payroll-table-card">
      <Table responsive hover className="payroll-table mb-0">
        <thead>
          <tr>
            <th>Name</th>
            <th>Fixed Salary (PKR)</th>
            <th>Per-Customer Commission (PKR)</th>
            <th>Commission Type</th>
            <th style={{ width: 90 }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {employees.filter(e => e.role !== 'Admin').map(emp => {
            const c = settingsMap[emp.email] || {};
            return (
              <tr key={emp.email}>
                <td className="fw-semibold">
                  {emp.name}
                  <span className="badge rounded-pill ms-2" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)', fontSize: 10 }}>
                    {emp.role}
                  </span>
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    type="number"
                    value={c.fixedSalary ?? ''}
                    placeholder="0"
                    onChange={e => onChange(emp.email, 'fixedSalary', e.target.value)}
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    type="number"
                    value={c.perCustomerCommission ?? ''}
                    placeholder="0"
                    onChange={e => onChange(emp.email, 'perCustomerCommission', e.target.value)}
                  />
                </td>
                <td>
                  <Form.Select
                    size="sm"
                    value={c.commissionType || 'flat'}
                    onChange={e => onChange(emp.email, 'commissionType', e.target.value)}
                  >
                    <option value="flat">Flat (PKR per customer)</option>
                    <option value="percent">Percent of sale</option>
                  </Form.Select>
                </td>
                <td>
                  <Button
                    size="sm"
                    variant="primary"
                    style={{ borderRadius: 7, fontSize: 12 }}
                    onClick={() => save(emp)}
                  >
                    Save
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

function TeamPayroll({ user, onOpenSlip, onOpenUpload, reloadKey }) {
  const now      = new Date();
  const [data,    setData]    = useState([]);
  const [month,   setMonth]   = useState(now.getMonth() + 1);
  const [year,    setYear]    = useState(now.getFullYear());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/commissions/payroll-team?month=${month}&year=${year}`)
      .then(r => setData(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [month, year, reloadKey]);

  const total = data.reduce((s, r) => s + Number(r.totalEarnings || 0), 0);

  return (
    <div className="d-flex flex-column gap-3" data-tour="payroll-admin">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div className="d-flex gap-2">
          <Form.Select
            size="sm"
            className="payroll-select-input"
            style={{ width: 130 }}
            value={month}
            onChange={e => setMonth(Number(e.target.value))}
          >
            {MONTHS.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </Form.Select>
          <Form.Select
            size="sm"
            className="payroll-select-input"
            style={{ width: 100 }}
            value={year}
            onChange={e => setYear(Number(e.target.value))}
          >
            {[now.getFullYear(), now.getFullYear() - 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </Form.Select>
        </div>

        {user?.role === 'Admin' && (
          <Button
            size="sm"
            variant="primary"
            className="d-flex align-items-center gap-1 ms-auto"
            style={{ borderRadius: 8, fontSize: 12.5 }}
            onClick={onOpenUpload}
          >
            <Upload size={13} /> Import Excel / CSV
          </Button>
        )}
      </div>

      {loading ? (
        <TableSkeleton rows={5} cols={10} />
      ) : (
        <div className="payroll-table-card">
          <Table responsive hover className="payroll-table mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Fixed</th>
                <th>Commission</th>
                <th>Bonuses</th>
                <th>Deductions</th>
                <th>Cars Sold</th>
                <th>Total Earnings</th>
                <th>Status</th>
                <th style={{ width: 90, textAlign: 'center' }}>Slip</th>
              </tr>
            </thead>
            <tbody>
              {data.map(r => (
                <tr key={r.employeeId}>
                  <td className="fw-semibold">{r.name}</td>
                  <td>
                    <span className="badge rounded-pill" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)', fontSize: 10 }}>
                      {r.role}
                    </span>
                  </td>
                  <td>{fmt(r.fixedSalary)}</td>
                  <td>{fmt(r.commission)}</td>
                  <td>{fmt(r.bonuses)}</td>
                  <td className="text-danger">{fmt(r.deductions)}</td>
                  <td>{r.carsSold || 0}</td>
                  <td className="fw-bold" style={{ color: 'var(--success)' }}>{fmt(r.totalEarnings)}</td>
                  <td>
                    <span className={`payroll-status-badge ${r.paymentStatus === 'paid' ? 'paid' : 'pending'}`}>
                      {r.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="d-inline-flex align-items-center gap-1"
                      style={{ borderRadius: 6, fontSize: 11.5, padding: '3px 8px' }}
                      onClick={() => onOpenSlip && onOpenSlip({
                        ...r,
                        month,
                        year,
                        employee: { id: r.employeeId, name: r.name, role: r.role }
                      })}
                      title="View & Download Pay Slip"
                    >
                      <FileText size={12} /> Slip
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            {data.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={7} className="fw-bold">Total Payout</td>
                  <td className="fw-bold" style={{ color: 'var(--success)' }}>{fmt(total)}</td>
                  <td />
                  <td />
                </tr>
              </tfoot>
            )}
          </Table>
        </div>
      )}
    </div>
  );
}

export default function Payroll() {
  const { user } = useAuth();
  const [employees,   setEmployees]   = useState([]);
  const [settingsMap, setSettingsMap] = useState({});
  const [tab,         setTab]         = useState('overview');

  // Modals state
  const [slipModalOpen, setSlipModalOpen]       = useState(false);
  const [selectedSlipData, setSelectedSlipData] = useState(null);
  const [uploadModalOpen, setUploadModalOpen]   = useState(false);
  const [reloadKey, setReloadKey]               = useState(0);

  const handleOpenSlip = (slipData) => {
    setSelectedSlipData(slipData);
    setSlipModalOpen(true);
  };

  const handleOpenUpload = () => {
    setUploadModalOpen(true);
  };

  const handleUploadSuccess = () => {
    setReloadKey(k => k + 1);
  };

  useEffect(() => {
    Promise.all([api.get('/employees'), api.get('/commissions/settings')])
      .then(([empRes, setRes]) => {
        setEmployees(Array.isArray(empRes.data) ? empRes.data : []);
        const map = {};
        (setRes.data || []).forEach(s => {
          const email = s.employee?.email;
          if (email) {
            map[email] = {
              perCustomerCommission: s.perCustomerCommission ?? 0,
              fixedSalary: s.fixedSalary ?? 0,
              commissionType: s.commissionType || 'flat'
            };
          }
        });
        setSettingsMap(map);
      })
      .catch(() => {});
  }, [reloadKey]);

  const isAdmin   = user?.role === 'Admin';
  const isManager = user?.role === 'Manager';

  return (
    <div className="crm-page payroll-page">
      {/* ── Page Header ── */}
      <div className="payroll-header">
        <div>
          <h2 className="payroll-title">Payroll Management</h2>
          <div className="payroll-subtitle">
            Unified salary, sales commission, and compensation statements — PKR
          </div>
        </div>

        {/* ── Segmented Navigation Tabs ── */}
        <div className="payroll-tabs">
          <button
            type="button"
            className={`payroll-tab-btn ${tab === 'overview' ? 'active' : ''}`}
            onClick={() => setTab('overview')}
          >
            <LayoutDashboard size={14} /> Overview
          </button>
          {isAdmin && (
            <button
              type="button"
              className={`payroll-tab-btn ${tab === 'settings' ? 'active' : ''}`}
              onClick={() => setTab('settings')}
            >
              <Sliders size={14} /> Settings
            </button>
          )}
          <button
            type="button"
            className={`payroll-tab-btn ${tab === 'history' ? 'active' : ''}`}
            onClick={() => setTab('history')}
          >
            <History size={14} /> History
          </button>
          {(isAdmin || isManager) && (
            <button
              type="button"
              className={`payroll-tab-btn ${tab === 'team' ? 'active' : ''}`}
              onClick={() => setTab('team')}
            >
              <Users size={14} /> Team
            </button>
          )}
        </div>
      </div>

      {/* ── Tab Content ── */}
      <div>
        {tab === 'overview' && (
          <PayrollOverview
            user={user}
            employees={employees}
            onOpenSlip={handleOpenSlip}
            onOpenUpload={handleOpenUpload}
            reloadKey={reloadKey}
          />
        )}
        {tab === 'settings' && isAdmin && (
          <PayrollSettings
            employees={employees}
            settingsMap={settingsMap}
            setSettingsMap={setSettingsMap}
          />
        )}
        {tab === 'history' && (
          <PayrollHistory
            user={user}
            employees={employees}
            onOpenSlip={handleOpenSlip}
          />
        )}
        {tab === 'team' && (isAdmin || isManager) && (
          <TeamPayroll
            user={user}
            onOpenSlip={handleOpenSlip}
            onOpenUpload={handleOpenUpload}
            reloadKey={reloadKey}
          />
        )}
      </div>

      {/* ── Modals ── */}
      <SalarySlipModal
        show={slipModalOpen}
        onHide={() => setSlipModalOpen(false)}
        data={selectedSlipData}
      />

      <PayrollUploadModal
        show={uploadModalOpen}
        onHide={() => setUploadModalOpen(false)}
        employees={employees}
        onSuccess={handleUploadSuccess}
      />
    </div>
  );
}
